import { useMemo, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TreeConfig {
  parent_key: string;
  type: "ancestry" | "parent_id";
  label_key?: string;
  code_key?: string;
}

interface TreeNode {
  id: string;
  parentId: string | null;
  label: string;
  code?: string | null;
}

interface ResourceTreeProps {
  records: Array<Record<string, unknown>>;
  tree: TreeConfig;
  expandAll?: boolean;
  showPath?: string;
  destroyPath?: string;
}

export default function ResourceTree({
  records,
  tree,
  expandAll = false,
  showPath,
  destroyPath,
}: ResourceTreeProps) {
  const { nodes, childrenById, roots } = useMemo(() => {
    const parsedNodes: TreeNode[] = records.map((record) => {
      const id = String(record.id);
      const parentId = resolveParentId(record, tree);
      const labelKey = tree.label_key || "name";
      const codeKey = tree.code_key || "code";
      const label = String(record[labelKey] ?? record.name ?? id);
      const code = record[codeKey] ? String(record[codeKey]) : null;
      return { id, parentId, label, code };
    });

    const childrenMap = new Map<string, TreeNode[]>();
    parsedNodes.forEach((node) => {
      if (!node.parentId) return;
      const list = childrenMap.get(node.parentId) || [];
      list.push(node);
      childrenMap.set(node.parentId, list);
    });

    const allIds = new Set(parsedNodes.map((node) => node.id));
    const rootNodes = parsedNodes.filter((node) => !node.parentId || !allIds.has(node.parentId));
    return { nodes: parsedNodes, childrenById: childrenMap, roots: rootNodes };
  }, [records, tree]);

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (!expandAll) return new Set();
    return new Set(nodes.map((node) => node.id));
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  if (records.length === 0) {
    return <div className="py-10 text-center text-muted-foreground">لا توجد بيانات لعرضها.</div>;
  }

  return (
    <ul className="space-y-1">
      {roots.map((node) => (
        <TreeNodeRow
          key={node.id}
          node={node}
          level={0}
          childrenById={childrenById}
          expanded={expanded}
          onToggle={toggle}
          selectedId={selectedId}
          onSelect={setSelectedId}
          showPath={showPath}
          destroyPath={destroyPath}
        />
      ))}
    </ul>
  );
}

function TreeNodeRow({
  node,
  level,
  childrenById,
  expanded,
  onToggle,
  selectedId,
  onSelect,
  showPath,
  destroyPath,
}: {
  node: TreeNode;
  level: number;
  childrenById: Map<string, TreeNode[]>;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  showPath?: string;
  destroyPath?: string;
}) {
  const children = childrenById.get(node.id) || [];
  const isExpanded = expanded.has(node.id);
  const hasChildren = children.length > 0;
  const isSelected = selectedId === node.id;
  const showLink = showPath ? showPath.replace("__ID__", node.id) : "";
  const destroyLink = destroyPath ? destroyPath.replace("__ID__", node.id) : "";

  return (
    <li>
      <div
        className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/60"
        style={{ paddingInlineStart: `${level * 20 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="flex h-6 w-6 items-center justify-center rounded-md border border-border/60 text-muted-foreground"
            aria-label={isExpanded ? "طي الفرع" : "توسيع الفرع"}
          >
            <ChevronLeft
              className={cn("h-4 w-4 transition-transform", isExpanded && "-rotate-90")}
            />
          </button>
        ) : (
          <span className="h-6 w-6" />
        )}
        <button
          type="button"
          onClick={() => {
            onSelect(node.id);
            if (hasChildren) onToggle(node.id);
          }}
          className="text-sm font-medium text-foreground underline-offset-4 group-hover:underline"
        >
          {node.code ? `${node.code} - ${node.label}` : node.label}
        </button>
        {isSelected && (
          <div className="pr-4">
            <div className="inline-flex items-center overflow-hidden rounded-full border border-border bg-muted/50 text-xs">
              {showLink && (
                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  className="h-6 rounded-none px-2.5 text-foreground hover:bg-muted text-xs leading-none"
                >
                  <Link href={showLink}>تعديل</Link>
                </Button>
              )}
              {showLink && destroyLink && <span className="h-5 w-px bg-border" />}
              {destroyLink && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 rounded-none px-2.5 text-destructive hover:bg-destructive/10 text-xs leading-none"
                    >
                      حذف
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader className="text-right">
                      <AlertDialogTitle>هل أنت متأكد؟</AlertDialogTitle>
                      <AlertDialogDescription>سيتم حذف العنصر بشكل دائم.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>إلغاء</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-white hover:bg-destructive/90"
                        onClick={() => router.delete(destroyLink)}
                      >
                        حذف
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul className="space-y-1">
          {children.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              level={level + 1}
              childrenById={childrenById}
              expanded={expanded}
              onToggle={onToggle}
              selectedId={selectedId}
              onSelect={onSelect}
              showPath={showPath}
              destroyPath={destroyPath}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function resolveParentId(record: Record<string, unknown>, tree: TreeConfig): string | null {
  if (tree.type === "ancestry") {
    const ancestry = record[tree.parent_key];
    if (!ancestry) return null;
    const parts = String(ancestry).split("/").filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  }

  const parentValue = record[tree.parent_key];
  if (!parentValue) return null;
  return String(parentValue);
}
